/**
 * API: HTTP client and utilities.
 * @see {@link https://github.com/axios/axios}
 * @module api
 */
"use strict";

import axios, { type AxiosInstance, type AxiosResponse } from "axios";
import LinkHeader from "http-link-header";

import { getBaseURL, getCachedAccountToken } from "@/contexts/auth/utils";
import {
  getTruthSessionIdRequestHeaders,
  omitTruthSessionIdHeader,
} from "@/utils/truth-session-id";
import {
  VISITOR_TOKEN_HEADER,
  getVisitorTokenRequestHeaders,
  isPepeEndpoint,
  omitVisitorTokenHeader,
  persistVisitorTokenFromHeaders,
} from "@/utils/visitor-token";

import type MockAdapter from "axios-mock-adapter";

/**
  Parse Link headers, mostly for pagination.
  @see {@link https://www.npmjs.com/package/http-link-header}
  @param {object} response - Axios response object
  @returns {object} Link object
  */
export const getLinks = (response: AxiosResponse): LinkHeader => {
  return new LinkHeader(response.headers?.link);
};

export const getNextLink = (response: AxiosResponse): string | undefined => {
  return getLinks(response).refs.find((link) => link.rel === "next")?.uri;
};

export const getPrevLink = (response: AxiosResponse): string | undefined => {
  return getLinks(response).refs.find((link) => link.rel === "prev")?.uri;
};

const maybeParseJSON = (data: string) => {
  try {
    return JSON.parse(data);
  } catch {
    return data;
  }
};

function applyVisitorTokenToClient(
  client: AxiosInstance,
  headers: Record<string, unknown> | undefined,
): void {
  persistVisitorTokenFromHeaders(headers);

  const token = headers?.["x-visitor-token"];

  if (typeof token === "string" && token.length > 0) {
    client.defaults.headers.common[VISITOR_TOKEN_HEADER] = token;
  }
}

/**
 * Base client for HTTP requests.
 * @param {string} accessToken
 * @param {string} baseURL
 * @returns {object} Axios instance
 */
export const baseClient = (
  accessToken?: string | null,
  baseURL: string = "",
): AxiosInstance => {
  const url = baseURL || getBaseURL();
  const isAuthenticated = Boolean(accessToken);

  const client = axios.create({
    // When BACKEND_URL is set, always use it.
    baseURL: url,
    headers: Object.assign(
      accessToken
        ? {
            Authorization: `Bearer ${accessToken}`,
          }
        : getVisitorTokenRequestHeaders(),
    ),
    transformResponse: [maybeParseJSON],
  });

  client.interceptors.request.use((config) => {
    if (isPepeEndpoint(config.url)) {
      omitTruthSessionIdHeader(config.headers);
    } else {
      Object.assign(config.headers, getTruthSessionIdRequestHeaders());
    }

    return config;
  });

  if (!isAuthenticated) {
    client.interceptors.request.use((config) => {
      if (isPepeEndpoint(config.url)) {
        omitVisitorTokenHeader(config.headers);
      }

      return config;
    });

    client.interceptors.response.use(
      (response) => {
        if (!isPepeEndpoint(response.config.url)) {
          applyVisitorTokenToClient(client, response.headers);
        }

        return response;
      },
      (error) => {
        if (!isPepeEndpoint(error.config?.url)) {
          applyVisitorTokenToClient(client, error.response?.headers);
        }

        return Promise.reject(error);
      },
    );
  }

  return client;
};

export const api = () => baseClient(null, "");

/**
 * Stateful API client.
 * @returns {object} Axios instance
 */
export default (): AxiosInstance => {
  const accessToken = getCachedAccountToken();
  const baseURL = getBaseURL();

  return baseClient(accessToken, baseURL);
};

// The test mock exports these, so they're needed for TypeScript.
export const __stub = (_func: (mock: MockAdapter) => void) => 0;
export const __clear = (): Function[] => [];
